
import React, { useState } from 'react';

const AIReports: React.FC = () => {
  const [chatMessages, setChatMessages] = useState([
    {
      id: 1,
      type: 'ai',
      content: '您好！我是您的AI数据分析助手。请告诉我您想了解什么数据，我会为您生成相应的报表。',
      timestamp: '10:30'
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const quickQuestions = [
    '显示本月项目收入分析',
    '生成客户满意度报告',
    '分析项目完成率趋势',
    '展示技术栈使用统计',
    '查看团队工作效率数据'
  ];

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
    };

    setChatMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsGenerating(true);

    // 模拟AI生成报表
    setTimeout(() => {
      const aiResponse = {
        id: Date.now() + 1,
        type: 'ai',
        content: generateAIResponse(inputMessage),
        timestamp: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }),
        hasChart: true
      };
      setChatMessages(prev => [...prev, aiResponse]);
      setIsGenerating(false);
    }, 2000);
  };

  const generateAIResponse = (question: string) => {
    if (question.includes('收入') || question.includes('财务')) {
      return '根据您的要求，我已生成本月项目收入分析报表。数据显示本月总收入285,000元，较上月增长15%。主要收入来源：区块链项目占40%，AI系统开发占35%，传统开发占25%。';
    } else if (question.includes('客户') || question.includes('满意度')) {
      return '客户满意度报告已生成。本月客户满意度平均分4.8/5.0，较上月提升0.2分。满意度最高的服务类型是区块链开发(4.9分)，其次是AI系统开发(4.8分)。';
    } else if (question.includes('完成率') || question.includes('进度')) {
      return '项目完成率趋势分析完成。近6个月平均完成率92%，呈稳步上升趋势。按时完成率最高的是小程序开发(95%)，区块链项目由于技术复杂度较高完成率为88%。';
    } else {
      return '我已为您生成相关数据报表。如需更详细的分析，请提供具体的数据维度或时间范围。';
    }
  };

  const handleQuickQuestion = (question: string) => {
    setInputMessage(question);
  };

  return (
    <div className="space-y-6">
      {/* Page Title */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">AI智能报表</h1>
        <p className="text-gray-600 mt-1">通过对话生成您需要的数据分析报表</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chat Interface */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-96 flex flex-col">
            {/* Chat Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                <i className="ri-robot-line text-white"></i>
              </div>
              <div className="ml-3">
                <h3 className="font-semibold text-gray-900">AI数据分析助手</h3>
                <p className="text-sm text-green-600">在线</p>
              </div>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {chatMessages.map((message) => (
                <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                    message.type === 'user' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-100 text-gray-900'
                  }`}>
                    <p className="text-sm">{message.content}</p>
                    <p className={`text-xs mt-1 ${
                      message.type === 'user' ? 'text-blue-200' : 'text-gray-500'
                    }`}>
                      {message.timestamp}
                    </p>
                    {message.hasChart && (
                      <div className="mt-3 p-3 bg-white rounded border">
                        <div className="h-32 bg-gradient-to-r from-blue-100 to-purple-100 rounded flex items-center justify-center">
                          <i className="ri-bar-chart-line text-3xl text-blue-600"></i>
                        </div>
                        <p className="text-xs text-gray-600 mt-2">📊 数据图表已生成</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {isGenerating && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 text-gray-900 px-4 py-2 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                      <span className="text-sm">正在生成报表...</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Chat Input */}
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex space-x-3">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder="请描述您想要的数据报表..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputMessage.trim() || isGenerating}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-send-plane-line"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions & Reports */}
        <div className="space-y-6">
          {/* Quick Questions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">快速提问</h3>
            <div className="space-y-2">
              {quickQuestions.map((question, index) => (
                <button
                  key={index}
                  onClick={() => handleQuickQuestion(question)}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
                >
                  {question}
                </button>
              ))}
            </div>
          </div>

          {/* Recent Reports */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">最近报表</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-900">月度收入分析</p>
                  <p className="text-xs text-gray-500">2024-01-15</p>
                </div>
                <button className="text-blue-600 hover:text-blue-700 cursor-pointer">
                  <i className="ri-download-line"></i>
                </button>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-900">客户满意度报告</p>
                  <p className="text-xs text-gray-500">2024-01-12</p>
                </div>
                <button className="text-blue-600 hover:text-blue-700 cursor-pointer">
                  <i className="ri-download-line"></i>
                </button>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-900">项目进度统计</p>
                  <p className="text-xs text-gray-500">2024-01-10</p>
                </div>
                <button className="text-blue-600 hover:text-blue-700 cursor-pointer">
                  <i className="ri-download-line"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIReports;
